import { useState } from 'react';
import { Download, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { contractManagementService } from '../services/contract-management.service';
import { getDraftGoogleDocOpenUrl, getGoogleDocOpenUrl } from '../utils/contract-status';

type Props = {
  googleDocUrl?: string | null;
  googleDocId?: string | null;
  googleDocWebViewUrl?: string | null;
  draft?: { id?: string; embedEditUrl?: string | null; googleDocId: string } | null;
  contractId?: string;
  showDownload?: boolean;
  showRefreshPermissions?: boolean;
  size?: 'sm' | 'default';
  onDownloadComplete?: () => void;
  onRefreshPermissionsComplete?: () => void;
  className?: string;
};

function useContractDocumentActionState({
  googleDocUrl,
  googleDocId,
  googleDocWebViewUrl,
  draft,
  contractId,
  showDownload = true,
  showRefreshPermissions = true,
  onDownloadComplete,
  onRefreshPermissionsComplete,
}: Props) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRefreshingPermissions, setIsRefreshingPermissions] = useState(false);
  const { toast } = useToast();

  const docUrl =
    googleDocUrl ??
    (draft ? getDraftGoogleDocOpenUrl(draft) : null) ??
    getGoogleDocOpenUrl({ googleDocId, googleDocWebViewUrl });
  const refreshTarget = draft?.id
    ? { kind: 'draft' as const, id: draft.id }
    : contractId && googleDocId
      ? { kind: 'contract' as const, id: contractId }
      : null;

  const handleDownload = async () => {
    if (!contractId || isDownloading) return;
    setIsDownloading(true);
    try {
      await contractManagementService.downloadContractFile(contractId);
      onDownloadComplete?.();
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRefreshPermissions = async () => {
    if (!refreshTarget || isRefreshingPermissions) return;
    setIsRefreshingPermissions(true);
    try {
      const result =
        refreshTarget.kind === 'draft'
          ? await contractManagementService.refreshDraftGoogleDocPermissions(refreshTarget.id)
          : await contractManagementService.refreshContractGoogleDocPermissions(refreshTarget.id);
      toast({
        title: 'Permissões recarregadas',
        description:
          result.emails.length > 0
            ? `${result.emails.length} e-mail(s) sincronizado(s) com o Google Drive.`
            : 'Nenhum e-mail configurado para sincronizar.',
      });
      onRefreshPermissionsComplete?.();
    } catch (err: any) {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao recarregar permissões',
        variant: 'destructive',
      });
    } finally {
      setIsRefreshingPermissions(false);
    }
  };

  return {
    docUrl,
    refreshTarget,
    isDownloading,
    isRefreshingPermissions,
    handleDownload,
    handleRefreshPermissions,
    showDownload,
    showRefreshPermissions,
    contractId,
  };
}

export function ContractDocumentActionMenuItems(props: Props) {
  const {
    docUrl,
    refreshTarget,
    isDownloading,
    isRefreshingPermissions,
    handleDownload,
    handleRefreshPermissions,
    showDownload,
    showRefreshPermissions,
    contractId,
  } = useContractDocumentActionState(props);

  return (
    <>
      {docUrl && (
        <DropdownMenuItem asChild>
          <a
            href={docUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-4 w-4" />
            Abrir no Google Docs
          </a>
        </DropdownMenuItem>
      )}
      {showRefreshPermissions && refreshTarget && (
        <DropdownMenuItem
          onSelect={(e) => {
            e.stopPropagation();
            void handleRefreshPermissions();
          }}
          disabled={isRefreshingPermissions}
        >
          {isRefreshingPermissions ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {isRefreshingPermissions ? 'Recarregando...' : 'Recarregar permissões'}
        </DropdownMenuItem>
      )}
      {showDownload && contractId && (
        <DropdownMenuItem
          onSelect={(e) => {
            e.stopPropagation();
            void handleDownload();
          }}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {isDownloading ? 'Baixando...' : 'Baixar contrato (PDF)'}
        </DropdownMenuItem>
      )}
    </>
  );
}

export function ContractDocumentActions(props: Props) {
  const {
    docUrl,
    refreshTarget,
    isDownloading,
    isRefreshingPermissions,
    handleDownload,
    handleRefreshPermissions,
    showDownload,
    showRefreshPermissions,
    contractId,
  } = useContractDocumentActionState(props);
  const { size = 'default', className } = props;

  return (
    <div className={`flex flex-wrap gap-2 ${className ?? ''}`}>
      {docUrl && (
        <Button variant="outline" size={size} asChild>
          <a
            href={docUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-4 w-4 mr-1.5" />
            Abrir no Google Docs
          </a>
        </Button>
      )}
      {showRefreshPermissions && refreshTarget && (
        <Button
          variant="outline"
          size={size}
          onClick={(e) => {
            e.stopPropagation();
            void handleRefreshPermissions();
          }}
          disabled={isRefreshingPermissions}
        >
          {isRefreshingPermissions ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-1.5" />
          )}
          {isRefreshingPermissions ? 'Recarregando...' : 'Recarregar permissões'}
        </Button>
      )}
      {showDownload && contractId && (
        <Button
          variant="default"
          size={size}
          onClick={(e) => {
            e.stopPropagation();
            void handleDownload();
          }}
          disabled={isDownloading}
        >
          {isDownloading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
          {isDownloading ? 'Baixando...' : 'Baixar contrato (PDF)'}
        </Button>
      )}
    </div>
  );
}
